import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Mail, Globe } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { PasswordInput } from "@/components/PasswordInput";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LanguageContext";
import { formatApiErrorDetail } from "@/lib/api";

export const googleLogin = () => {
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const redirectUrl = window.location.origin + "/";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
};

export function AuthShell({ title, sub, children, footer }) {
  const { lang, toggleLang } = useLang();
  return (
    <div className="min-h-screen flex items-center justify-center bg-white px-4 py-10">
      <div className="w-full max-w-md bg-white rounded-3xl border border-[#EFEBE0] shadow-xl p-8 animate-fade-up">
        <div className="flex justify-end">
          <button onClick={toggleLang} className="flex items-center gap-1.5 text-sm font-semibold text-[#1A5C3A]" data-testid="language-switcher">
            <Globe className="w-4 h-4" />{lang === "ar" ? "EN" : "ع"}
          </button>
        </div>
        <Link to="/" className="flex items-center justify-center mb-6">
          <img src="/bazaroo-logo.png?v=4" alt="bazaroo" className="h-16 w-auto object-contain" />
        </Link>
        <h1 className="font-headline text-2xl font-bold text-center mb-1">{title}</h1>
        <p className="text-slate-500 text-center text-sm mb-6">{sub}</p>
        {children}
        {footer && <p className="text-center text-sm text-slate-500 mt-6">{footer}</p>}
      </div>
    </div>
  );
}

export function EmailInput({ value, onChange, testid }) {
  return (
    <div className="relative">
      <Mail className="w-4 h-4 absolute top-4 start-3 text-slate-400" />
      <Input type="email" required value={value} onChange={onChange} className="ps-9 h-12 rounded-xl" placeholder="you@example.com" dir="ltr" data-testid={testid} />
    </div>
  );
}

export function GoogleButton({ testid }) {
  const { t } = useLang();
  return (
    <Button type="button" onClick={googleLogin} variant="outline" className="w-full rounded-xl h-12 gap-2 font-semibold" data-testid={testid}>
      <img src="https://www.google.com/favicon.ico" alt="" className="w-4 h-4" /> {t("auth_google")}
    </Button>
  );
}

export default function Login() {
  const { login } = useAuth();
  const { t } = useLang();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await login(email, password);
      if (res?.verification_required) { navigate("/verify-email", { state: { email: res.email } }); return; }
      toast.success(t("auth_loginSuccess"));
      navigate("/");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || t("auth_loginFail"));
    } finally { setLoading(false); }
  };

  const footer = <>{t("auth_noAccount")} <Link to="/register" className="text-[#1A5C3A] font-semibold" data-testid="go-to-register">{t("auth_createAccount")}</Link></>;

  return (
    <AuthShell title={t("auth_welcomeBack")} sub={t("auth_loginSub")} footer={footer}>
      <form onSubmit={submit} className="space-y-4" data-testid="login-form">
        <div>
          <Label className="mb-1.5 block">{t("auth_email")}</Label>
          <EmailInput value={email} onChange={(e) => setEmail(e.target.value)} testid="login-email-input" />
        </div>
        <div>
          <Label className="mb-1.5 block">{t("auth_password")}</Label>
          <PasswordInput value={password} onChange={(e) => setPassword(e.target.value)} testid="login-password-input" />
        </div>
        <Button type="submit" disabled={loading} className="w-full h-12 rounded-xl bazaro-gradient text-white font-semibold" data-testid="login-submit-button">
          {loading ? t("auth_signingIn") : t("auth_signIn")}
        </Button>
        <div className="text-center">
          <Link to="/forgot-password" className="text-sm text-[#1A5C3A] font-semibold hover:underline" data-testid="forgot-password-link">{t("auth_forgot")}</Link>
        </div>
      </form>
      <div className="flex items-center gap-3 my-5 text-slate-400 text-xs">
        <div className="h-px bg-slate-200 flex-1" /> {t("auth_or")} <div className="h-px bg-slate-200 flex-1" />
      </div>
      <GoogleButton testid="google-login-button" />
    </AuthShell>
  );
}
